import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Node, NodeConnection, NodeDataMapping, NodeRunInstance, RunInstance, WorkflowOutput, NodeExecutionCondition, NodeTestRunInstance } from "../types/workflow";
import customFetch from "../utils/customFetch";
import { NodeConfig } from "../types/nodes";

export const useGetNodesByWorkflowId = () => {
    return useMutation<Node[], ErrorEvent, { workflowId: string }>({
        mutationFn: ({ workflowId }) => {
          return customFetch(`/v1/nodes?workflow=${workflowId}`)
            .then(async(res) => {
              const data = await res.json();
              return res.ok
                ? data
                : Promise.reject(new Error(`Failed to fetch nodes for workflow ID ${workflowId}`))
            })
            .catch((err) => toast.error(err.message));
        }
      });
}

export const useGetNodeByNodeId = () => {
  return useMutation<Node, ErrorEvent, { nodeId: string }>({
      mutationFn: ({ nodeId }) => {
        return customFetch(`/v1/nodes/${nodeId}`)
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error(`Failed to fetch node with ID ${nodeId}`))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}

export const useDeleteNodeById = () => {
  return useMutation<null, ErrorEvent, { nodeId: string }>({
    mutationFn: ({ nodeId }) => {
      return customFetch(`/v1/nodes/${nodeId}`, {
        method: "DELETE"
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          toast.success("Node deleted successfuly");
          return data;
        }
        return Promise.reject(new Error(`Failed to delete node`))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useGetNodeConnectionsByWorkflowId = () => {
    return useMutation<NodeConnection[], ErrorEvent, { workflowId: string }>({
        mutationFn: ({ workflowId }) => {
          return customFetch(`/v1/nodeconnections?workflow=${workflowId}`)
            .then(async(res) => {
              const data = await res.json();
              return res.ok
                ? data
                : Promise.reject(new Error(`Failed to fetch nodeconnections for workflow ID ${workflowId}`))
            })
            .catch((err) => toast.error(err.message));
        }
      });
}

export const useGetNodeExecutionConditionByNodeConnection = () => {
  return useMutation<NodeExecutionCondition[], ErrorEvent, { nodeConnectionId: string }>({
      mutationFn: ({ nodeConnectionId }) => {
        return customFetch(`/v1/nodeexecutionconditions?nodeconnection=${nodeConnectionId}`)
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error(`Failed to fetch nodeconnections for nodeconnection ID ${nodeConnectionId}`))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}

export const usePostNodeExecutionCondition = () => {
  return useMutation<NodeExecutionCondition, ErrorEvent, { payload: NodeExecutionCondition }>({
      mutationFn: ({ payload }) => {
        return customFetch(`/v1/nodeexecutionconditions`,{
          method: "POST",
          body: JSON.stringify(payload)
        })
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error(`Failed to save conditions`))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}

export const usePatchNodeExecutionCondition = () => {
  return useMutation<NodeExecutionCondition, ErrorEvent, { payload: NodeExecutionCondition }>({
      mutationFn: ({ payload }) => {
        return customFetch(`/v1/nodeexecutionconditions/${payload.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        })
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error(`Failed to save conditions`))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}

export const useDeleteNodeExecutionCondition = () => {
  return useMutation<NodeExecutionCondition, ErrorEvent, { executionId: string }>({
      mutationFn: ({ executionId }) => {
        return customFetch(`/v1/nodeexecutionconditions/${executionId}`, {
          method: "DELETE"
        })
          .then(async(res) => {
            const data = await res.json();
            return res.ok
              ? data
              : Promise.reject(new Error(`Failed to delete conditions`))
          })
          .catch((err) => toast.error(err.message));
      }
    });
}


export type SaveNodeProps = {
  name: string;
  workflow: WorkflowOutput;
  action: string;
  service: string;
  config?: string;
}

export const useSaveNode = () => {
  return useMutation<Node, ErrorEvent, SaveNodeProps>({
    mutationFn: (nodeProps) => {
      return customFetch(`/v1/nodes`, {
        method: 'POST',
        body: JSON.stringify(nodeProps)
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to save node'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useSaveNodeWithPatch = () => {
  return useMutation<Node, ErrorEvent, Node>({
    mutationFn: (nodeProps) => {
      nodeProps.save_state = 0;
      return customFetch(`/v1/nodes/${nodeProps.id}`, {
        method: 'PATCH',
        body: JSON.stringify(nodeProps)
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to save node'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export type NodeConnectionProps = {
  workflow: WorkflowOutput;
  start_node: Node;
  end_node: Node;
}

export const useSaveNodeConnection = () => {
  return useMutation<NodeConnection, ErrorEvent, NodeConnectionProps>({
    mutationFn: (nodeConnectionProps) => {
      return customFetch(`/v1/nodeconnections`, {
        method: 'POST',
        body: JSON.stringify(nodeConnectionProps)
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to save node connection'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useUpdateNodeConnection = () => {
  return useMutation<NodeConnection, ErrorEvent, NodeConnection>({
    mutationFn: (connection) => {
      return customFetch(`/v1/nodeconnections/${connection.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          is_optional: connection.is_optional,
        })
      })
      .then(async(res) => {
        const data = await res.json();
        if (res.ok) {
          toast.success(`Connection ${connection.is_optional ? 'made optional' : 'made required'}`);
          return data;
        }
        const error = await res.json().catch(() => ({ message: 'Failed to update connection' }));
        return Promise.reject(new Error(error.message || 'Failed to update connection'));
      })
      .catch((err) => {
        toast.error(err.message);
        throw err;
      });
    }
  });
}

export const useDeleteNodeConnection = () => {
  return useMutation<null, ErrorEvent, { connectionId: string }>({
    mutationFn: ({ connectionId }) => {
      return customFetch(`/v1/nodeconnections/${connectionId}`, {
        method: 'DELETE'
      })
      .then(async(res) => {
        if (res.ok) {
          toast.success('Connection deleted successfully');
          return null;
        }
        return Promise.reject(new Error('Failed to delete connection'));
      })
      .catch((err) => {
        toast.error(err.message);
        throw err;
      });
    }
  });
}

export type NodeDataMappingProps = {
  workflow: WorkflowOutput;
  start_node_data: string;
  end_node_data: string;
  node_connection: NodeConnection;
  user_id: string;
  id?: string;
  ref_node_id?: string;
}

export const useSaveNodeDataMappings = () => {
  return useMutation<NodeDataMapping, ErrorEvent, NodeDataMappingProps>({
    mutationFn: (nodeDataMapping) => {
      return customFetch('/v1/nodedatamappings', {
        method: 'POST',
        body: JSON.stringify(nodeDataMapping)
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to save node data mappings'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useResetNodeDataMappings = () => {
  return useMutation<{
    workflow_id: string;
    node_id: string;
    run_config: {
      key: string;
      ref_node_id: string;
      value: string;
    }[];
  }, ErrorEvent, { nodeId: string, runConfig: { key: string; ref_node_id: string; value: string; }[], workflowId: string }>({
    mutationFn: ({ nodeId, runConfig, workflowId }) => {
      return customFetch(`/v1/nodedatamappings/reset`, {
        method: 'POST',
        body: JSON.stringify({ node_id: nodeId, run_config: runConfig, workflow_id: workflowId })
      })
        .then(async(res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to reset node data mappings'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const useGetConfigsForNode = () => {
  return useMutation<NodeConfig, ErrorEvent, { nodeId: string }>({
    mutationFn: ({ nodeId }) => {
      return customFetch(`/v1/nodes/getConfigs/${nodeId}`)
        .then(async (res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get node configs'))
        })
        .catch((err) => {
          console.log(err);
        });
    }
  });
};

export const useExecuteTestRunForNode = () => {
  return useMutation<NodeTestRunInstance, ErrorEvent, { runInstanceId: string, node: Partial<Node> }>({
    mutationFn: ({ node, runInstanceId }) => {
      const payload = {
        run_instance_id: runInstanceId,
        node_id: node.id,
      }
      return customFetch(`/v1/testRun/execute `, {
        method: "POST",
        body: JSON.stringify(payload),
      })
        .then(async (res) => {
          const data = await res.json();
          if (!res.ok) {
            toast.error('Failed to execute test run')
          }
          return data;
        })
    }
  });
}

export const useDeleteTriggerTestRun = (onSuccess: () => void) => {
  return useMutation<null, ErrorEvent, { nodeId: string }>({
    mutationFn: ({ nodeId }) => {
      return customFetch(`/v1/testRun/delete-trigger-runs?node_id=${nodeId}`, { method: 'DELETE' })
        .then(async (res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to delete trigger test run'))
        })
        .catch((err) => toast.error(err.message));
    },
    onSuccess
  });
}

export const useGetNodeTestRunInstance = (nodeId?: string) => {
  return useQuery<{ nodeTestRunInstance: NodeTestRunInstance }, ErrorEvent>({
    queryKey: ['nodetestruninstance', nodeId],
    queryFn: () => {
      return customFetch(`/v1/testRun/instance?node=${nodeId}`)
        .then(async (res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get outputs for test run'))
        })
        .catch((err) => toast.error(err.message));
    },
    retry: false,
    enabled: !!nodeId
  });
};

export const useGetTestRunInputsForEachNode = () => {
  return useMutation<
    { inputs: { run_instance: RunInstance; node_run_instances: NodeRunInstance[] }[] },
    ErrorEvent,
    { nodeId: string, reset: boolean }
  >({
    mutationFn: ({ nodeId, reset }) => {
      return customFetch(`/v1/testRun/getInputs?nodes=${nodeId}&reset=${reset}`)
        .then(async (res) => {
          const data = await res.json();
          return res.ok
            ? data
            : Promise.reject(new Error('Failed to get inputs for test run'))
        })
        .catch((err) => toast.error(err.message));
    }
  });
}

export const useUpdateNodeName = () => {
  return useMutation<Node, ErrorEvent, { nodeId: string, name: string }>({
    mutationFn: ({ nodeId, name }) => {
      return customFetch(`/v1/nodes/${nodeId}/name`, {
        method: 'PATCH',
        body: JSON.stringify({ name })
      })
      .then(async(res) => {
        const data = await res.json();
        if(res.ok) {
          toast.success("Node name updated successfully");
          return data;
        }
        return Promise.reject(new Error(`Failed to update node name`))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}

export const useInsertNodeBetween = () => {
  return useMutation<Node, ErrorEvent, { startNodeId: string, endNodeId: string, node: SaveNodeProps }>({
    mutationFn: ({ startNodeId, endNodeId, node }) => {
      return customFetch(`/v1/nodes/add-between`, {
        method: 'POST',
        body: JSON.stringify({ start_node: startNodeId, end_node: endNodeId, node })
      })
      .then(async(res) => {
        const data = await res.json();
        return res.ok
          ? data
          : Promise.reject(new Error('Failed to insert node between'))
      })
      .catch((err) => toast.error(err.message));
    }
  });
}